import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Briefcase, Clock, DollarSign, AlertTriangle, TrendingUp, Activity,
  Calendar as CalendarIcon, ArrowRight, User, Inbox,
} from 'lucide-react';
import { Card, CardHeader, CardBody, KPI, Badge, EmptyState, MiniBarChart } from '../components/ui';
import { SetupChecklistWidget } from '../components/onboarding/OnboardingHelpers';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { PERMISSIONS } from '../lib/permissions';
import {
  listMatters, listDeadlines, listTimeEntries, listInvoices,
  clientTrustBalances, listActivity, listAllMatterPayments, listAllCaseCharges,
  listPortalDocumentRequestsForFirm, listContacts, listFirmUsers,
} from '../lib/db';
import NextActionWidget from '../components/onboarding/NextActionWidget';
import { formatMoney, formatMoneyCompact, formatDate, formatRelative, formatDuration } from '../lib/formatters';
import { withTimeout } from '../lib/fetchWithTimeout';
import { useRoute } from '../lib/router';

// ─── Resizable panel row ──────────────────────────────────────────────────────
// On mobile: stacks vertically. On desktop (≥1024px): side-by-side with a
// draggable divider between each pair. Widths are persisted to localStorage.
function ResizablePanelRow({ id, children, defaultWidths, minPx = 180 }) {
  const childArray = React.Children.toArray(children);
  const count = childArray.length;

  // Track whether we're in mobile mode
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [widths, setWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`rpr:${id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === count) return parsed;
      }
    } catch {}
    return defaultWidths || Array(count).fill(100 / count);
  });

  const containerRef = useRef(null);

  const startDrag = useCallback((dividerIdx, e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const totalPx = container.getBoundingClientRect().width;
    const startX = e.clientX;
    const startW = [...widths];
    const minPct = (minPx / totalPx) * 100;

    const onMove = (mv) => {
      const deltaPct = ((mv.clientX - startX) / totalPx) * 100;
      const next = [...startW];
      next[dividerIdx]     = Math.max(minPct, startW[dividerIdx]     + deltaPct);
      next[dividerIdx + 1] = Math.max(minPct, startW[dividerIdx + 1] - deltaPct);
      const available = startW[dividerIdx] + startW[dividerIdx + 1];
      if (next[dividerIdx] + next[dividerIdx + 1] > available) {
        next[dividerIdx + 1] = available - next[dividerIdx];
      }
      setWidths(next);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      setWidths((prev) => {
        localStorage.setItem(`rpr:${id}`, JSON.stringify(prev));
        return prev;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [id, widths, minPx]);

  // Mobile: simple vertical stack
  if (isMobile) {
    return (
      <div className="space-y-4">
        {childArray.map((child, i) => <div key={i}>{child}</div>)}
      </div>
    );
  }

  // Desktop: draggable side-by-side layout
  return (
    <div ref={containerRef} className="flex items-stretch gap-0">
      {childArray.map((child, i) => (
        <React.Fragment key={i}>
          <div style={{ width: `${widths[i]}%`, flexShrink: 0, flexGrow: 0, minWidth: 0, overflow: 'hidden' }}>
            {child}
          </div>
          {i < count - 1 && (
            <div
              onMouseDown={(e) => startDrag(i, e)}
              title="Drag to resize"
              className="flex-shrink-0 flex items-center justify-center w-3 cursor-col-resize group select-none"
            >
              <div className="w-0.5 h-10 rounded-full bg-slate-200 dark:bg-slate-700 group-hover:bg-indigo-400 dark:group-hover:bg-indigo-500 transition-colors" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { firmId, firm, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const { can } = usePermissions();
  const canViewFirmFinances = can(PERMISSIONS.FINANCE_VIEW_FIRM);
  const [, navigate] = useRoute();
  const [state, setState] = useState({
    matters: [], deadlines: [], timeEntries: [], invoices: [], trust: [], activity: [],
    payments: [], caseCharges: [],
    docRequests: [],
    contacts: [], teamCount: 1,
    loading: true, error: null,
  });

  useEffect(() => {
    if (!firmId) { setState((s) => ({ ...s, loading: false })); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        // Scope time entries to the current calendar year — the dashboard only
        // shows MTD and 14-day views, so earlier entries aren't needed here.
        const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
        const [m, d, t, i, tb, a, p, ch, dr, co, fu] = await withTimeout(Promise.all([
          listMatters(firmId),
          listDeadlines(firmId, true),
          listTimeEntries(firmId, { since: yearStart, limit: 1000 }),
          listInvoices(firmId),
          clientTrustBalances(firmId),
          listActivity(firmId, 8),
          listAllMatterPayments(firmId),
          listAllCaseCharges(firmId),
          listPortalDocumentRequestsForFirm(),
          listContacts(firmId, 'client', { limit: 50 }),
          listFirmUsers(firmId),
        ]), 20000);
        setState({
          matters:     m.data  || [],
          deadlines:   d.data  || [],
          timeEntries: t.data  || [],
          invoices:    i.data  || [],
          trust:       tb.data || [],
          activity:    a.data  || [],
          payments:    p.data  || [],
          caseCharges: ch.data || [],
          docRequests: Array.isArray(dr.data) ? dr.data : [],
          contacts:    co.data || [],
          teamCount:   (fu.data || []).length || 1,
          loading: false, error: null,
        });
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: err?.message || 'Failed to load dashboard.' }));
      }
    })();
  }, [firmId, refreshSignal]);

  const kpis = useMemo(() => {
    const activeMatters = state.matters.filter((m) => m.status === 'open').length;
    const mtdStart = new Date(); mtdStart.setDate(1); mtdStart.setHours(0,0,0,0);

    // Billable time entries
    const mtdEntries    = state.timeEntries.filter((e) => new Date(e.entry_date) >= mtdStart);
    const billableMin   = mtdEntries.filter((e) => e.billable).reduce((s, e) => s + (e.duration_minutes || 0), 0);
    const billableRevMtd= mtdEntries.filter((e) => e.billable).reduce((s, e) => s + Number(e.amount || 0), 0);

    // Flat fees collected (payments table) — all-time and MTD
    const flatFeesTotal = state.payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const flatFeesMtd   = state.payments
      .filter((p) => p.payment_date && new Date(p.payment_date) >= mtdStart)
      .reduce((s, p) => s + Number(p.amount || 0), 0);

    // Extra charges (case_charges table) — all-time and MTD
    const extraChargesTotal = state.caseCharges.reduce((s, c) => s + Number(c.amount || 0), 0);
    const extraChargesMtd   = state.caseCharges
      .filter((c) => c.charge_date && new Date(c.charge_date) >= mtdStart)
      .reduce((s, c) => s + Number(c.amount || 0), 0);

    const arOutstanding = state.invoices
      .filter((inv) => ['sent','viewed','partial','overdue'].includes(inv.status))
      .reduce((s, inv) => s + Number(inv.balance || 0), 0);
    const trustTotal = state.trust.reduce((s, c) => s + Number(c.total || 0), 0);
    const next7 = Date.now() + 7 * 86400000;
    const upcoming = state.deadlines.filter((d) => new Date(d.due_date).getTime() <= next7).length;

    return {
      activeMatters, billableMin, billableRevMtd,
      flatFeesTotal, flatFeesMtd,
      extraChargesTotal, extraChargesMtd,
      arOutstanding, trustTotal, upcoming,
    };
  }, [state]);

  // 14-day billable-time revenue spark
  const recentRevenue = useMemo(() => {
    const days = 14;
    return Array.from({ length: days }, (_, i) => {
      const dt = new Date(); dt.setDate(dt.getDate() - (days - 1 - i));
      const key = dt.toISOString().slice(0,10);
      const sum = state.timeEntries
        .filter((e) => e.entry_date === key && e.billable)
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      return { label: key.slice(5), value: sum };
    });
  }, [state.timeEntries]);

  // 14-day flat fees spark (payments table)
  const recentFlatFees = useMemo(() => {
    const days = 14;
    return Array.from({ length: days }, (_, i) => {
      const dt = new Date(); dt.setDate(dt.getDate() - (days - 1 - i));
      const key = dt.toISOString().slice(0,10);
      const sum = state.payments
        .filter((p) => p.payment_date === key)
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      return { label: key.slice(5), value: sum };
    });
  }, [state.payments]);

  // 14-day extra charges spark (case_charges table)
  const recentExtraCharges = useMemo(() => {
    const days = 14;
    return Array.from({ length: days }, (_, i) => {
      const dt = new Date(); dt.setDate(dt.getDate() - (days - 1 - i));
      const key = dt.toISOString().slice(0,10);
      const sum = state.caseCharges
        .filter((c) => c.charge_date === key)
        .reduce((s, c) => s + Number(c.amount || 0), 0);
      return { label: key.slice(5), value: sum };
    });
  }, [state.caseCharges]);

  // ── My Revenue (scoped to the logged-in employee) ───────────────────────────
  const myRevenue = useMemo(() => {
    if (!userProfile?.id) return null;
    const myId = userProfile.id;

    // Billable time entries logged by me
    const myEntries = state.timeEntries.filter((e) => e.user_id === myId && e.billable);
    const myBillableRev = myEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const myBillableMin = myEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0);

    // Matters I am the responsible attorney for
    const myMatterIds = new Set(
      state.matters.filter((m) => m.responsible_attorney_id === myId).map((m) => m.id)
    );
    const myMatters = state.matters.filter((m) => myMatterIds.has(m.id));

    // Flat fees collected on my matters
    const myFlatFees = state.payments
      .filter((p) => myMatterIds.has(p.matter_id))
      .reduce((s, p) => s + Number(p.amount || 0), 0);

    // Extra charges on my matters
    const myExtraCharges = state.caseCharges
      .filter((c) => myMatterIds.has(c.matter_id))
      .reduce((s, c) => s + Number(c.amount || 0), 0);

    // Per-matter breakdown (top 5 by total revenue)
    const perMatter = myMatters.map((m) => {
      const billable = state.timeEntries
        .filter((e) => e.matter_id === m.id && e.user_id === myId && e.billable)
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      const fees = state.payments
        .filter((p) => p.matter_id === m.id)
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      const charges = state.caseCharges
        .filter((c) => c.matter_id === m.id)
        .reduce((s, c) => s + Number(c.amount || 0), 0);
      return { id: m.id, title: m.title, number: m.matter_number, billable, fees, charges, total: billable + fees + charges };
    }).filter((m) => m.total > 0).sort((a, b) => b.total - a.total).slice(0, 5);

    return {
      myBillableRev, myBillableMin,
      myFlatFees, myExtraCharges,
      myMatterCount: myMatters.length,
      total: myBillableRev + myFlatFees + myExtraCharges,
      perMatter,
    };
  }, [state, userProfile]);

  const recentMatters = state.matters.slice(0, 5);

  if (state.error) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center px-4">
      <p className="text-sm text-red-600 dark:text-red-400 max-w-sm">{state.error}</p>
      <button
        onClick={() => setState((s) => ({ ...s, loading: true, error: null }))}
        className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
      >
        Retry
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {firm?.name || 'Your firm'} · {formatDate(new Date(), { style: 'long' })}
          </p>
        </div>
      </div>

      {/* New-firm setup checklist — auto-hides once dismissed or all items checked */}
      <SetupChecklistWidget />

      {/* Smart "next action" — surfaces ONE concrete next step based on data.
          Self-hides when nothing actionable remains, or when dismissed for the session. */}
      <NextActionWidget
        contacts={state.contacts}
        matters={state.matters}
        timeEntries={state.timeEntries}
        invoices={state.invoices}
        payments={state.payments}
        teamCount={state.teamCount}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI loading={state.loading} label="Active case files"  value={kpis.activeMatters}               icon={Briefcase}     tone="indigo" />
        <KPI loading={state.loading} label="Billable (MTD)"     value={formatDuration(kpis.billableMin)} icon={Clock}         tone="green" />
        {canViewFirmFinances ? (
          <KPI loading={state.loading} label="AR outstanding"    value={formatMoneyCompact(kpis.arOutstanding)}     title={formatMoney(kpis.arOutstanding)}     icon={DollarSign}    tone="amber" />
        ) : (
          <KPI loading={state.loading} label="My billable (MTD)" value={formatMoneyCompact(myRevenue?.myBillableRev || 0)} title={formatMoney(myRevenue?.myBillableRev || 0)} icon={DollarSign}    tone="amber" />
        )}
        <KPI loading={state.loading} label="Deadlines (7d)"     value={kpis.upcoming}                    icon={AlertTriangle} tone="red" />
      </div>

      {/* Client document review queue */}
      {state.docRequests.some((r) => r.status === 'uploaded') && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <CardHeader
            title="Client uploads awaiting review"
            subtitle="Documents your clients submitted via the portal"
            action={<Inbox size={16} className="text-amber-500" />}
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-amber-100 dark:divide-amber-900/30">
              {state.docRequests
                .filter((r) => r.status === 'uploaded')
                .slice(0, 5)
                .map((r) => (
                  <li
                    key={r.id}
                    onClick={() => r.matter_id && navigate(`/matters/${r.matter_id}`)}
                    className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-amber-100/40 dark:hover:bg-amber-900/20"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {r.title}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        From {[r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || 'client'}
                        {r.matter_title ? ` · ${r.matter_title}` : ''}
                        {r.uploaded_at ? ` · ${formatRelative(r.uploaded_at)}` : ''}
                      </p>
                    </div>
                    <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
                  </li>
                ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* ── Revenue collected section ── (firm-wide; gated by finance.view_firm) */}
      {canViewFirmFinances && (
      <ResizablePanelRow id="dash-revenue" defaultWidths={[33.33, 33.33, 33.34]}>

        {/* Flat fees collected */}
        <Card>
          <CardHeader
            title="Flat fees collected"
            subtitle="Installment payments on case files"
            action={<TrendingUp size={16} className="text-slate-400" />}
          />
          <CardBody>
            {/* text-3xl can blow out the card width on narrow third-
                column layouts when the total runs into 7+ digits.
                Compact + tooltip keeps the big number glanceable and
                still lets the user see exact cedis on hover. */}
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 truncate"
               title={formatMoney(kpis.flatFeesTotal)}>
              {formatMoneyCompact(kpis.flatFeesTotal)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              MTD: <span className="font-medium" title={formatMoney(kpis.flatFeesMtd)}>{formatMoneyCompact(kpis.flatFeesMtd)}</span>
            </p>
            <div className="mt-4">
              <MiniBarChart data={recentFlatFees} height={48} />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{recentFlatFees[0]?.label}</span>
                <span>{recentFlatFees[recentFlatFees.length - 1]?.label}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Extra charges */}
        <Card>
          <CardHeader
            title="Extra charges"
            subtitle="Disbursements, court fees & other"
            action={<TrendingUp size={16} className="text-slate-400" />}
          />
          <CardBody>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 truncate"
               title={formatMoney(kpis.extraChargesTotal)}>
              {formatMoneyCompact(kpis.extraChargesTotal)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              MTD: <span className="font-medium" title={formatMoney(kpis.extraChargesMtd)}>{formatMoneyCompact(kpis.extraChargesMtd)}</span>
            </p>
            <div className="mt-4">
              <MiniBarChart data={recentExtraCharges} height={48} />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{recentExtraCharges[0]?.label}</span>
                <span>{recentExtraCharges[recentExtraCharges.length - 1]?.label}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Combined revenue summary */}
        <Card>
          <CardHeader title="Revenue summary" subtitle="All-time · across all cases" />
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Billable hours</span>
                </div>
                <span className="text-sm font-semibold">{formatMoney(kpis.billableRevMtd)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Flat fees</span>
                </div>
                <span className="text-sm font-semibold">{formatMoney(kpis.flatFeesTotal)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Extra charges</span>
                </div>
                <span className="text-sm font-semibold">{formatMoney(kpis.extraChargesTotal)}</span>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Total collected</span>
                <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {formatMoney(kpis.billableRevMtd + kpis.flatFeesTotal + kpis.extraChargesTotal)}
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate('/billing')}
              className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              View billing detail <ArrowRight size={12} />
            </button>
          </CardBody>
        </Card>
      </ResizablePanelRow>
      )}

      {/* Trust balance + billable hours chart */}
      {canViewFirmFinances && (
      <ResizablePanelRow id="dash-trust" defaultWidths={[33.33, 66.67]}>
        <Card>
          <CardHeader title="Client Account balance" subtitle="Funds held across all clients" />
          <CardBody>
            <p className="text-3xl font-semibold truncate" title={formatMoney(kpis.trustTotal)}>
              {formatMoneyCompact(kpis.trustTotal)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {state.trust.length} client ledger{state.trust.length === 1 ? '' : 's'}
            </p>
            <button
              onClick={() => navigate('/trust')}
              className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Open client ledger <ArrowRight size={12} />
            </button>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Billable hours revenue"
            subtitle={`MTD: ${formatMoney(kpis.billableRevMtd)} · last 14 days`}
            action={<TrendingUp size={16} className="text-slate-400" />}
          />
          <CardBody>
            <MiniBarChart data={recentRevenue} height={72} />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{recentRevenue[0]?.label}</span>
              <span>{recentRevenue[recentRevenue.length - 1]?.label}</span>
            </div>
          </CardBody>
        </Card>
      </ResizablePanelRow>
      )}

      {/* ── My Revenue ─────────────────────────────────────────────────────── */}
      {myRevenue && (
        <Card>
          <CardHeader
            title="My revenue"
            subtitle={`${myRevenue.myMatterCount} case${myRevenue.myMatterCount !== 1 ? 's' : ''} as responsible attorney`}
            action={<User size={16} className="text-slate-400" />}
          />
          <CardBody>
            {/* Summary row */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
                <p className="text-xs text-indigo-500 font-medium uppercase tracking-wide mb-1">Billable hours</p>
                <p className="text-xl font-bold text-indigo-700 dark:text-indigo-300 truncate" title={formatMoney(myRevenue.myBillableRev)}>
                  {formatMoneyCompact(myRevenue.myBillableRev)}
                </p>
                <p className="text-xs text-indigo-400 mt-0.5">{formatDuration(myRevenue.myBillableMin)}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800">
                <p className="text-xs text-emerald-500 font-medium uppercase tracking-wide mb-1">Flat fees</p>
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300 truncate" title={formatMoney(myRevenue.myFlatFees)}>
                  {formatMoneyCompact(myRevenue.myFlatFees)}
                </p>
                <p className="text-xs text-emerald-400 mt-0.5">Installments collected</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
                <p className="text-xs text-amber-500 font-medium uppercase tracking-wide mb-1">Extra charges</p>
                <p className="text-xl font-bold text-amber-700 dark:text-amber-300 truncate" title={formatMoney(myRevenue.myExtraCharges)}>
                  {formatMoneyCompact(myRevenue.myExtraCharges)}
                </p>
                <p className="text-xs text-amber-400 mt-0.5">Disbursements & fees</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-900 dark:bg-white/10 border border-slate-700 dark:border-white/10">
                <p className="text-xs text-slate-300 font-medium uppercase tracking-wide mb-1">Total generated</p>
                <p className="text-xl font-bold text-white truncate" title={formatMoney(myRevenue.total)}>
                  {formatMoneyCompact(myRevenue.total)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">All sources</p>
              </div>
            </div>

            {/* Per-matter breakdown. Five-column table doesn't fit
                on phones — at < 560px we render a stacked card list
                instead so nothing gets clipped off the right edge. */}
            {myRevenue.perMatter.length > 0 ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">Top cases by revenue generated</p>

                {/* Mobile: stacked card list. Compact money so nothing
                    overflows even at 320px viewport. */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  {myRevenue.perMatter.map((m) => (
                    <li key={m.id} className="px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">{m.title}</p>
                          <p className="text-xs text-slate-400">{m.number}</p>
                        </div>
                        <p className="font-mono font-bold text-sm text-slate-900 dark:text-white flex-shrink-0"
                           title={formatMoney(m.total)}>
                          {formatMoneyCompact(m.total)}
                        </p>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        {m.billable > 0 && (
                          <span title={`Hours fees: ${formatMoney(m.billable)}`}>
                            <span className="text-slate-400">Hours:</span>{' '}
                            <span className="font-mono">{formatMoneyCompact(m.billable)}</span>
                          </span>
                        )}
                        {m.fees > 0 && (
                          <span title={`Flat fees: ${formatMoney(m.fees)}`}>
                            <span className="text-slate-400">Flat:</span>{' '}
                            <span className="font-mono text-emerald-600 dark:text-emerald-400">{formatMoneyCompact(m.fees)}</span>
                          </span>
                        )}
                        {m.charges > 0 && (
                          <span title={`Extra charges: ${formatMoney(m.charges)}`}>
                            <span className="text-slate-400">Extra:</span>{' '}
                            <span className="font-mono text-amber-600 dark:text-amber-400">{formatMoneyCompact(m.charges)}</span>
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Desktop / tablet: original table with horizontal
                    scroll as a safety net when the card panel is narrow. */}
                <div className="hidden sm:block rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Case</th>
                        {/* Header used to say "Billable hrs" but the
                            value is the cedi total of billable time
                            entries, not the hours themselves. */}
                        <th className="text-right px-4 py-2 font-medium">Hours fees</th>
                        <th className="text-right px-4 py-2 font-medium">Flat fees</th>
                        <th className="text-right px-4 py-2 font-medium">Extra charges</th>
                        <th className="text-right px-4 py-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {myRevenue.perMatter.map((m) => (
                        <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="px-4 py-2.5">
                            <p className="font-medium text-slate-800 dark:text-slate-200 truncate">{m.title}</p>
                            <p className="text-xs text-slate-400">{m.number}</p>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-slate-600 dark:text-slate-400">{formatMoney(m.billable)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{m.fees > 0 ? formatMoney(m.fees) : '—'}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-amber-600 dark:text-amber-400">{m.charges > 0 ? formatMoney(m.charges) : '—'}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 dark:text-white">{formatMoney(m.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No revenue recorded yet on your cases.</p>
            )}
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming deadlines */}
        <Card>
          <CardHeader
            title="Upcoming deadlines"
            action={<button onClick={() => navigate('/calendar')} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">View all</button>}
          />
          <CardBody className="p-0">
            {!state.deadlines.length ? (
              <EmptyState icon={CalendarIcon} title="No open deadlines" description="New deadlines will appear here." />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {state.deadlines.slice(0, 5).map((d) => {
                  const daysLeft = Math.ceil((new Date(d.due_date).getTime() - Date.now()) / 86400000);
                  const tone = daysLeft < 3 ? 'red' : daysLeft < 7 ? 'amber' : 'slate';
                  return (
                    <li key={d.id} className="flex items-center gap-3 px-6 py-3 text-sm">
                      <Badge tone={tone}>{daysLeft}d</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{d.title}</p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{d.matter?.matter_number || '—'} · {formatDate(d.due_date)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Recent matters */}
        <Card>
          <CardHeader
            title="Recent matters"
            action={<button onClick={() => navigate('/matters')} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">View all</button>}
          />
          <CardBody className="p-0">
            {!recentMatters.length ? (
              <EmptyState icon={Briefcase} title="No matters yet" description="Create your first matter to get started." />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {recentMatters.map((m) => (
                  <li
                    key={m.id}
                    onClick={() => navigate(`/matters/${m.id}`)}
                    className="flex items-center gap-3 px-6 py-3 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{m.title}</p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {m.matter_number} · {m.practice_area || m.matter_type || '—'}
                      </p>
                    </div>
                    <Badge tone={m.status === 'open' ? 'green' : 'slate'}>{m.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Activity stream */}
        <Card>
          <CardHeader title="Recent activity" />
          <CardBody className="p-0">
            {!state.activity.length ? (
              <EmptyState icon={Activity} title="Nothing yet" description="Firm activity will appear here." />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {state.activity.map((a) => (
                  <li key={a.id} className="px-6 py-3 text-sm">
                    <p className="truncate">{a.summary || a.action}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{formatRelative(a.created_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
